import type { MutableRefObject } from 'react';
import { useEffect } from 'react';
import { css } from '@eti/styles';
import { Collection, DevBar } from '@eti/debug';
import { useSnowplowTracking, useTranslation } from '@eti/providers';
import { Helmet } from 'react-helmet';
import { SeatMapModalProvider } from '@eti/products';
import { vars } from '@etg/wings';
import { useHistory } from 'react-router-dom';
import { isDev } from '../../../../../common/debug/utils';
import { DebugButton, DebugCommonComponents } from '../../../../../common/debug/components';
import Header from '../../../common/Header';
import { routes } from '../../../../../constants/routesAndApisConstants';
import type { OrderTypePostbooking } from '../../utils/OrderUtils';
import ExtraProductsForm from './ExtraProductsForm';

const pageWrapperStyles = (isOrderDetails?: boolean) => css`
  background: ${isOrderDetails ? 'unset' : vars.colors.pageBackdrop};
  height: 100%;
`;

export type ExtraProductsPageTypes = {
  contactEmail: string | null;
  destinationCityCode: string;
  fillForm: () => void;
  form: string;
  isLoading: boolean;
  onSubmit: () => void;
  order: OrderTypePostbooking;
  reset: () => void;
  tabHeading1Ref?: MutableRefObject<HTMLHeadingElement | null>;
};

const ExtraProductsPage = ({
  contactEmail,
  destinationCityCode,
  fillForm,
  form,
  isLoading,
  onSubmit,
  order,
  reset,
  tabHeading1Ref,
}: ExtraProductsPageTypes) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const isOrderDetailsPage = history.location.pathname.includes(routes.ORDER_DETAILS);

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Post Booking Products',
        action: 'Seen',
        label: 'Page',
      });
    }
  }, [isSnowplowTrackingEnabled, sendTrackEvent]);

  return (
    <div className={pageWrapperStyles(isOrderDetailsPage)}>
      <Helmet title={t('PageTitle.postbooking.products.action')} />
      {!isOrderDetailsPage && <Header destinationCityCode={destinationCityCode} />}
      <SeatMapModalProvider>
        <ExtraProductsForm
          contactEmail={contactEmail}
          form={form}
          isLoading={isLoading}
          onSubmit={onSubmit}
          order={order}
          tabHeading1Ref={tabHeading1Ref}
        />
      </SeatMapModalProvider>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugButton onClick={fillForm}>Fill form</DebugButton>
            <DebugButton onClick={reset}>Reset Form</DebugButton>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </div>
  );
};

export default ExtraProductsPage;
